import { resolveStrapiBaseUrl } from '@outpoll/strapi-client/utils';
import { createHttpClient, type NextFetchOptions } from '@/lib/api/httpClient';
import type { StrapiCommentsResponseDto, StrapiCommentVerboseDto } from '@/lib/api/strapi/types';
import { mapComment } from './mappers';
import type { CommentItem, CommentSource, CommentsPage, CommentsSort } from './models';

const BASE_URL = resolveStrapiBaseUrl();
const http = createHttpClient({ baseUrl: BASE_URL });

const DEFAULT_PAGE_SIZE = 20;

function sourceQuery(source: CommentSource): Record<string, string> {
  return source.kind === 'event' ? { eventId: source.eventId } : { slug: source.slug };
}

function sourceBody(source: CommentSource): Record<string, string> {
  return source.kind === 'event' ? { eventId: source.eventId } : { newsId: source.newsId };
}

export interface FetchCommentsPageParams {
  sort?: CommentsSort;
  page?: number;
  pageSize?: number;
  onlyHolders?: boolean;
  onlyMine?: boolean;
}

export async function fetchCommentsPage(
  source: CommentSource,
  params: FetchCommentsPageParams = {},
  options?: { next?: NextFetchOptions },
): Promise<CommentsPage> {
  const dto = await http.get<StrapiCommentsResponseDto>('/api/comments', {
    query: {
      ...sourceQuery(source),
      sort: params.sort ?? 'createdAt:desc',
      page: params.page ?? 1,
      pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
      ...(params.onlyHolders ? { onlyHolders: true } : {}),
      ...(params.onlyMine ? { onlyMine: true } : {}),
    },
    next: options?.next,
  });

  return {
    comments: (dto.data ?? []).map(mapComment),
    pagination: dto.meta?.pagination ?? {
      page: 1,
      pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
      pageCount: 0,
      total: 0,
    },
  };
}

/**
 * Server-side helper for SSR prefetch when `newsId` isn't resolved yet.
 * Uses `slug` alone, which is all the GET endpoint needs.
 */
export function fetchCommentsPageBySlug(
  slug: string,
  params: FetchCommentsPageParams = {},
  options?: { next?: NextFetchOptions },
): Promise<CommentsPage> {
  return fetchCommentsPage({ kind: 'news', slug, newsId: '' }, params, options);
}

export interface CreateCommentParams {
  content: string;
  parentCommentId?: string;
}

export async function createComment(
  source: CommentSource,
  params: CreateCommentParams,
): Promise<CommentItem> {
  const response = await http.post<{ data: StrapiCommentVerboseDto } | StrapiCommentVerboseDto>(
    '/api/comments',
    {
      body: {
        data: {
          content: params.content,
          ...sourceBody(source),
          ...(params.parentCommentId ? { parentCommentId: params.parentCommentId } : {}),
        },
      },
    },
  );

  const dto =
    'data' in response && response.data ? response.data : (response as StrapiCommentVerboseDto);

  if (!dto || !dto.i) {
    throw new Error('Invalid response format from comment creation API');
  }

  return mapComment(dto);
}

export async function editComment(commentId: string, content: string): Promise<void> {
  await http.put(`/api/comments/${commentId}`, { body: { data: { content } } });
}

export async function deleteComment(commentId: string): Promise<void> {
  await http.delete(`/api/comments/${commentId}`);
}

export async function likeComment(commentId: string): Promise<void> {
  await http.post(`/api/comments/${commentId}/like`);
}

export async function dislikeComment(commentId: string): Promise<void> {
  await http.post(`/api/comments/${commentId}/dislike`);
}

export async function removeCommentReaction(commentId: string): Promise<void> {
  await http.delete(`/api/comments/${commentId}/reaction`);
}
